// API client for indication submissions.
//
// POSTs submissions to the Lambda-backed API at api.insurance.zacharycompanies.ai
// — the Lambda validates, persists to DynamoDB, and sends an SES email to the
// broker. The server ignores the client-computed `output` and recomputes its
// own authoritative result.

import type { IndicationInput, IndicationOutput } from '@zg/shared';

export interface SubmissionPayload {
  submissionId: string;
  email: string;
  input: IndicationInput;
  output: IndicationOutput;
}

export interface PostIndicationResult {
  ok: boolean;
}

const API_ORIGIN =
  (typeof window !== 'undefined' && window.location.hostname === 'localhost')
    ? 'https://api.insurance.zacharycompanies.ai'
    : 'https://api.insurance.zacharycompanies.ai';

/**
 * Fire-and-forget POST to /indication. Resolves {ok:true} on 2xx. Any
 * network or server error resolves {ok:false} with a console.error — the
 * caller intentionally doesn't block the user experience on this.
 */
export async function postIndication(
  payload: SubmissionPayload,
): Promise<PostIndicationResult> {
  try {
    const res = await fetch(`${API_ORIGIN}/indication`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        submissionId: payload.submissionId,
        email: payload.email,
        input: payload.input,
      }),
    });
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.error('[api] postIndication non-2xx', res.status, await res.text().catch(() => ''));
      return { ok: false };
    }
    return { ok: true };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[api] postIndication failed', err);
    return { ok: false };
  }
}
